import { FightAction } from '../models/fight';

/**
 * Um id legado só serve como identificador quando é inteiro positivo. `0` é o
 * sentinel de "sem mapeamento" usado no `initData`, e `null`/`undefined` vêm
 * de lutas que o fight-api ainda não mapeou.
 */
export function isUsableLegacyId(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/**
 * Sentinel enviado em `fightId` quando a luta não tem mapeamento legado.
 * O mesmo valor que o backend grava; a correlação real é pelo `fightNewId`.
 */
export const NO_LEGACY_FIGHT_ID = 0;

/**
 * Valor a enviar no campo `fightId` de uma action. Normaliza `null`,
 * `undefined` e `NaN` para o sentinel — `JSON.stringify(NaN)` vira `null`, que
 * o backend não conseguia distinguir de uma luta real e fazia o save falhar.
 */
export function toLegacyFightId(legacyId: unknown): number {
  return isUsableLegacyId(legacyId) ? legacyId : NO_LEGACY_FIGHT_ID;
}

/**
 * Decide se uma action recebida pertence à luta aberta na tela.
 *
 * O UUID é a chave confiável e vem em todo payload emitido pelo scout; o id
 * legado só entra como critério quando é utilizável dos dois lados. Na dúvida
 * a action é ACEITA: descartar em silêncio já custou a perda de todas as
 * atualizações em tempo real de lutas sem `legacyId`.
 */
export function belongsToFight(
  payload: Pick<FightAction, 'fightId' | 'fightNewId'> | undefined,
  fight: { id?: string; legacyId?: number | null },
): boolean {
  if (!payload) return true;

  if (payload.fightNewId && fight.id) {
    return payload.fightNewId === fight.id;
  }

  if (isUsableLegacyId(payload.fightId) && isUsableLegacyId(fight.legacyId)) {
    return payload.fightId === fight.legacyId;
  }

  return true;
}

/**
 * Versão fail-closed de `belongsToFight`, para operações DESTRUTIVAS
 * (`reset`, `remove`).
 *
 * A sala do socket é do evento inteiro, então o scout recebe o `reset` de todas
 * as lutas do card. Aceitar um por engano apaga o scout inteiro da luta aberta;
 * perder um legítimo custa um F5. Por isso, quando o payload não permite provar
 * a correspondência, a mensagem é DESCARTADA — o oposto de `belongsToFight`.
 */
export function belongsToFightStrict(
  payload: Pick<FightAction, 'fightId' | 'fightNewId'> | undefined,
  fight: { id?: string; legacyId?: number | null },
): boolean {
  // Sem payload é dispatch local (limpeza ao desmontar a página, `fetch`).
  if (!payload) return true;

  // Nenhuma luta aberta: o estado voltou para o `initData` mas o socket
  // continua na sala do evento. Nada do que chegar aqui é nosso.
  if (!fight.id && !isUsableLegacyId(fight.legacyId)) return false;

  if (payload.fightNewId && fight.id) {
    return payload.fightNewId === fight.id;
  }

  if (isUsableLegacyId(payload.fightId) && isUsableLegacyId(fight.legacyId)) {
    return payload.fightId === fight.legacyId;
  }

  return false;
}
